import {
  useEffect,
  useMemo,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

type ParticlePoint = {
  phase: number;
  latitude: number;
  field: number;
  jitter: number;
  order: number;
  band: number;
};

type ParticleFieldProps = {
  fill?: number;
  count?: number;
  opacityScale?: number;
  immersive?: boolean;
  testId?: string;
};

type MirrorAtmosphereProps = ParticleFieldProps & {
  scrim?: boolean;
  scrimVariant?: "normal" | "content";
};

type AtmosphericPageProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  atmosphere?: Omit<MirrorAtmosphereProps, "testId">;
  contained?: boolean;
  contentClassName?: string;
  testId?: string;
};

type Rgb = [number, number, number];

function readRgbToken(
  styles: CSSStyleDeclaration,
  name: string,
  fallback: Rgb,
): Rgb {
  const values = styles
    .getPropertyValue(name)
    .trim()
    .split(",")
    .map(value => Number.parseFloat(value.trim()));

  return values.length === 3 && values.every(Number.isFinite)
    ? values as Rgb
    : fallback;
}

function createParticlePoints(count: number): ParticlePoint[] {
  const points: ParticlePoint[] = [];
  let seed = 3313;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  for (let index = 0; index < count; index += 1) {
    const theta = Math.acos(1 - 2 * ((index + 0.5) / count));
    const phase = Math.PI * (1 + Math.sqrt(5)) * index;
    const latitude = Math.PI / 2 - theta;
    const longitude = phase % (Math.PI * 2);
    const field = 0.55 + 0.45 * (
      Math.sin(longitude * 3.1 + latitude * 2.2) * 0.5
      + Math.cos(longitude * 5.7 - latitude * 1.4) * 0.3
      + Math.sin(longitude * 9.3 + latitude * 4.9) * 0.2
    );

    points.push({
      phase,
      latitude,
      field,
      jitter: random(),
      order: random(),
      band: Math.floor(((latitude + Math.PI / 2) / Math.PI) * 7),
    });
  }

  return points;
}

export function MirrorParticleField({
  fill = 0.72,
  count = 7000,
  opacityScale = 0.4,
  immersive = false,
  testId = "mirror-particle-canvas",
}: ParticleFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const points = useMemo(() => createParticlePoints(count), [count]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    let animationFrame = 0;
    let lastDrawTime = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const frameInterval = 1000 / 30;

    const readTheme = () => {
      const styles = getComputedStyle(document.documentElement);
      return {
        cool: readRgbToken(styles, "--atmosphere-particle-cool-rgb", [63, 111, 90]),
        warm: readRgbToken(styles, "--atmosphere-particle-warm-rgb", [174, 133, 76]),
        neutral: readRgbToken(styles, "--atmosphere-particle-neutral-rgb", [83, 103, 96]),
        unfilled: readRgbToken(styles, "--atmosphere-particle-unfilled-rgb", [98, 119, 111]),
        grid: styles.getPropertyValue("--atmosphere-grid-color").trim()
          || "rgba(63,111,90,0.10)",
        composite: styles.getPropertyValue("--atmosphere-particle-composite").trim()
          || "source-over",
      };
    };
    let theme = readTheme();

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(window.innerWidth * ratio);
      canvas.height = Math.round(window.innerHeight * ratio);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    const draw = (now: number) => {
      if (!reducedMotion) animationFrame = requestAnimationFrame(draw);
      if (lastDrawTime && now - lastDrawTime < frameInterval) return;
      lastDrawTime = now;

      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = window.innerWidth;
      const height = window.innerHeight;
      const { cool, warm, neutral, unfilled, grid, composite } = theme;

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height * (immersive ? 0.46 : 0.34);
      const scale = Math.min(width, height) * (immersive ? 0.5 : 0.44);
      const yaw = reducedMotion ? 0 : now * 0.00006;
      const pitch = 0.32;
      const project = (x: number, y: number, z: number) => {
        const rotatedX = x * Math.cos(yaw) - z * Math.sin(yaw);
        let rotatedZ = x * Math.sin(yaw) + z * Math.cos(yaw);
        const rotatedY = y * Math.cos(pitch) - rotatedZ * Math.sin(pitch);
        rotatedZ = y * Math.sin(pitch) + rotatedZ * Math.cos(pitch);
        const perspective = 1 / (2.1 - rotatedZ * 0.5);
        return [
          centerX + rotatedX * scale * perspective * 1.7,
          centerY + rotatedY * scale * perspective * 1.7,
          rotatedZ,
          perspective,
        ] as const;
      };

      context.globalCompositeOperation = "source-over";
      context.strokeStyle = grid;
      context.globalAlpha = opacityScale;
      context.lineWidth = 0.6;
      for (const latitude of [-60, -30, 0, 30, 60]) {
        context.beginPath();
        let drawing = false;
        for (let longitude = 0; longitude <= 360; longitude += 6) {
          const lat = latitude * Math.PI / 180;
          const lon = longitude * Math.PI / 180;
          const projected = project(
            Math.cos(lat) * Math.cos(lon),
            Math.sin(lat),
            Math.cos(lat) * Math.sin(lon),
          );
          if (projected[2] < -0.1) {
            drawing = false;
            continue;
          }
          if (!drawing) context.moveTo(projected[0], projected[1]);
          else context.lineTo(projected[0], projected[1]);
          drawing = true;
        }
        context.stroke();
      }

      context.globalCompositeOperation = composite as GlobalCompositeOperation;
      context.globalAlpha = 1;
      for (const point of points) {
        const bandLatitude = (point.band / 6) * Math.PI - Math.PI / 2;
        const latitude = point.latitude + (bandLatitude - point.latitude) * 0.7;
        const theta = Math.PI / 2 - latitude;
        const radius = 1 + (point.field - 0.5) * 0.62;
        const projected = project(
          Math.sin(theta) * Math.cos(point.phase) * radius,
          Math.cos(theta) * radius,
          Math.sin(theta) * Math.sin(point.phase) * radius,
        );
        if (projected[2] < -0.16) continue;

        const depth = (projected[2] + 1) / 2;
        const rim = Math.pow(1 - Math.abs(projected[2]), 2.6);
        const filled = point.order < Math.min(0.95, fill);
        const endpoint = point.field < 0.5 ? cool : warm;
        const blend = Math.abs(point.field - 0.5) * 2;
        const color = endpoint.map((channel, index) => (
          channel + (neutral[index] - channel) * (1 - blend)
        ));
        const gray = (color[0] + color[1] + color[2]) / 3;
        const finalColor = color.map(channel => gray + (channel - gray));
        let opacity = (
          (0.2 + point.jitter * 0.42) * (0.26 + depth * 0.82)
          + rim * 0.26
        ) * opacityScale;
        if (!filled) opacity *= 0.16;
        if (opacity < 0.005) continue;

        const rgb = filled ? finalColor : unfilled;
        context.fillStyle = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${opacity})`;
        const size = Math.max(
          0.6,
          (0.7 + point.jitter * 0.9) * projected[3] * 1.35,
        );
        context.fillRect(projected[0], projected[1], size, size);
      }

      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
    };

    const redraw = () => {
      cancelAnimationFrame(animationFrame);
      lastDrawTime = 0;
      theme = readTheme();
      resize();
      draw(performance.now());
    };
    const themeObserver = new MutationObserver(redraw);

    resize();
    window.addEventListener("resize", redraw);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    draw(performance.now());

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", redraw);
      themeObserver.disconnect();
    };
  }, [fill, immersive, opacityScale, points]);

  return (
    <canvas
      ref={canvasRef}
      className="mirror-particle-field"
      data-testid={testId}
      data-opacity-scale={opacityScale}
      aria-hidden="true"
    />
  );
}

export function MirrorAtmosphere({
  fill = 0.72,
  count = 7000,
  opacityScale = 0.4,
  immersive = false,
  scrim = true,
  scrimVariant = "normal",
  testId = "mirror-particle-canvas",
}: MirrorAtmosphereProps) {
  return (
    <div
      className={`mirror-atmosphere${immersive ? " mirror-atmosphere--immersive" : ""}${scrimVariant === "content" ? " mirror-atmosphere--content" : ""}`}
      aria-hidden="true"
    >
      <MirrorParticleField
        fill={fill}
        count={count}
        opacityScale={opacityScale}
        immersive={immersive}
        testId={testId}
      />
      {scrim && <div className="mirror-atmosphere-scrim" />}
    </div>
  );
}

export function AtmosphericPage({
  children,
  atmosphere,
  contained = false,
  className = "",
  contentClassName = "",
  testId,
  ...props
}: AtmosphericPageProps) {
  const PageElement = contained ? "div" : "main";

  return (
    <PageElement
      {...props}
      className={`mirror-atmosphere-page${contained ? " mirror-atmosphere-page--contained" : ""}${className ? ` ${className}` : ""}`}
      data-testid={testId}
    >
      <MirrorAtmosphere {...atmosphere} />
      <div className={`mirror-atmosphere-content${contentClassName ? ` ${contentClassName}` : ""}`}>
        {children}
      </div>
    </PageElement>
  );
}